/**
 * @file Calendrier interactif de réservation avec gestion des disponibilités, exceptions et liste d'attente.
 * @module packages.common.components.table-reservation.components.ReservationCalendar
 *
 * Affiche un calendrier mensuel navigable au clavier (roving tabindex) ou par la souris.
 * Chaque cellule de jour calcule son état visuel (disponible, complet, fermé, liste d'attente,
 * exception) à partir de la map de disponibilité renvoyée par l'API, du planning hebdomadaire
 * et des exceptions de fermeture configurées. Un popover informe l'utilisateur quand un
 * service alternatif (midi/soir) reste disponible sur un jour fermé pour le service sélectionné.
 */

import React, {useMemo, useCallback, useState, useRef, useImperativeHandle} from 'react';
import PropTypes from 'prop-types';
import {
  format,
  startOfMonth,
  endOfMonth,
  isSameDay,
  isToday,
  isPast,
  addDays,
  getMonth,
  getYear,
  startOfDay,
  differenceInCalendarDays,
} from 'date-fns';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

// icons
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

// constants
import {AVAILABILITY_STATUS, SERVICE_TYPES} from '../constants/reservation';
import {isFixedHorizon, getDayOpeningInstant, isDayOpenForBooking} from '../utils/bookingHorizon';

// utils
import {isBeforeOpeningDay} from 'packages/common/utils/restaurant/preOpening';
import {findExceptionForDay} from 'packages/common/utils/restaurant/exceptionDayResolver';

// i18n
import {useLocales} from 'packages/common/locales';

// Accessibility
import {WEEKDAY_LABELS} from 'packages/common/hooks/useAccessibility';

// ----------------------------------------------------------------------

const WEEKDAYS = WEEKDAY_LABELS;

const CalendarDayStyle = styled(ButtonBase, {
  shouldForwardProp: (prop) =>
    prop !== 'isToday' &&
    prop !== 'isSelected' &&
    prop !== 'isAvailable' &&
    prop !== 'isPast' &&
    prop !== 'inMonth' &&
    prop !== 'isClosed' &&
    prop !== 'isExceptionClosed' &&
    prop !== 'isOutsideBookingWindow' &&
    prop !== 'isWaitlistOnly',
})(({
  theme,
  isToday,
  isSelected,
  isAvailable,
  isPast,
  inMonth,
  isClosed,
  isExceptionClosed,
  isOutsideBookingWindow,
  isWaitlistOnly,
}) => ({
  'width': '100%',
  'height': 36,
  'position': 'relative',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'borderRadius': '50%',
  'margin': '0 auto',
  'cursor': inMonth && (isAvailable || isWaitlistOnly) ? 'pointer' : 'default',
  'overflow': 'visible',
  'fontSize': theme.typography.body2.fontSize,
  'fontWeight': 500,
  'transition': theme.transitions.create(['background-color', 'border-color', 'color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),

  // Base styles
  ...(inMonth && {
    color: theme.palette.text.primary,
  }),
  ...(!inMonth && {
    color: 'transparent',
    backgroundColor: 'transparent',
    pointerEvents: 'none',
  }),

  // Past dates et jours fermés régulièrement - grisé et totalement inerte
  ...((isPast || (isClosed && !isExceptionClosed)) && inMonth && {
    color: theme.palette.text.disabled,
    backgroundColor: 'transparent',
    pointerEvents: 'none',
  }),

  // Jours hors période d'ouverture du calendrier (au-delà de la fenêtre de
  // réservation) - grisés comme les jours fermés, mais restent réceptifs au
  // survol/tap pour révéler la date d'ouverture via l'infobulle. Jamais
  // sélectionnables : curseur d'aide plutôt que pointeur.
  ...(isOutsideBookingWindow && inMonth && {
    color: theme.palette.text.disabled,
    backgroundColor: 'transparent',
    cursor: 'help',
  }),

  // Today - anneau pointillé vert si disponible, sinon gris pointillé
  ...(isToday && !isSelected && {
    border: isAvailable ?
      `2px dashed ${theme.palette.success.main}` :
      `2px dashed ${theme.palette.text.disabled}`,
    fontWeight: 'bold',
  }),

  // LOGIQUE SIMPLE : 4 états visuels (hors jours fermés régulièrement et jours
  // hors période d'ouverture du calendrier, déjà grisés ci-dessus)
  ...(!isSelected && inMonth && !isPast && !isOutsideBookingWindow && !(isClosed && !isExceptionClosed) && {
    // 🟢 VERT = Disponible (cliquable)
    ...(isAvailable && !isWaitlistOnly && {
      'backgroundColor': alpha(theme.palette.success.main, 0.12),
      'color': theme.palette.success.dark,
      '&:hover': {
        backgroundColor: alpha(theme.palette.success.main, 0.24),
      },
    }),

    // 🟠 ORANGE = Liste d'attente uniquement (cliquable)
    ...(isWaitlistOnly && {
      'backgroundColor': alpha(theme.palette.orange.light, 0.18),
      'color': theme.palette.orange.main,
      '&:hover': {
        backgroundColor: alpha(theme.palette.orange.light, 0.32),
      },
    }),

    // 🔴 ROUGE = Complet OU Exception de fermeture (non cliquable)
    ...(!isAvailable && !isWaitlistOnly && {
      'backgroundColor': alpha(theme.palette.error.main, 0.08),
      'color': theme.palette.error.main,
      'pointerEvents': 'none',
    }),
  }),

  // Selected - fond vert foncé (disponible) ou orange foncé (waitlist) avec texte blanc
  ...(isSelected && !isWaitlistOnly && {
    'backgroundColor': theme.palette.success.dark,
    'color': theme.palette.common.white,
    'fontWeight': 'bold',
    '&:hover': {
      backgroundColor: theme.palette.success.main,
    },
    'boxShadow': `0 0 0 3px ${alpha(theme.palette.success.main, 0.3)}`,
  }),
  ...(isSelected && isWaitlistOnly && {
    'backgroundColor': theme.palette.orange.main,
    'color': theme.palette.orange.contrastText,
    'fontWeight': 'bold',
    '&:hover': {
      backgroundColor: theme.palette.orange.light,
    },
    'boxShadow': `0 0 0 3px ${alpha(theme.palette.orange.light, 0.35)}`,
  }),

  // Hover - uniquement pour les jours disponibles
  '&:hover': {
    ...(isAvailable && !isSelected && inMonth && {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    }),
  },

  // Focus visible pour l'accessibilité clavier
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: theme.palette.primary.main,
    outlineOffset: '2px',
    zIndex: 1,
  },
}));

// ----------------------------------------------------------------------

/**
 * Cellule unitaire d'un jour du calendrier.
 *
 * Détermine son propre état de disponibilité à partir de la map d'API,
 * du planning hebdomadaire et des exceptions, puis rend le style visuel
 * et les interactions (clic, popover, accessibilité) correspondants.
 *
 * @param {Object} props
 * @param {Date} props.day - Date représentée par la cellule
 * @param {boolean} props.inMonth - Indique si la date appartient au mois affiché
 * @param {Object} props.availabilityMap - Map date (yyyy-MM-dd) vers statut de disponibilité
 * @param {Date} [props.selectedDate] - Date actuellement sélectionnée
 * @param {Function} props.onDateSelect - Callback de sélection de date
 * @param {Object} props.bookingConstraints - Contraintes de réservation (délais, fenêtre)
 * @param {Array} [props.exceptions] - Exceptions de fermeture/ouverture exceptionnelle
 * @param {string} [props.selectedService] - Service sélectionné (lunch/dinner)
 * @param {Object} [props.serviceSchedule] - Planning hebdomadaire des services
 * @param {Function} [props.onShowServiceInfo] - Callback pour afficher le popover d'information
 * @param {Date} [props.defaultFocusDate] - Date devant recevoir le focus par défaut (roving tabindex)
 * @param {Function} [props.onKeyDown] - Gestionnaire clavier délégué par le parent
 * @param {boolean} [props.hasFormula] - Indique si une formule à disponibilité temporelle existe pour cette date
 * @param {Function} [props.t] - Fonction de traduction générique
 * @param {Object} [props.dateFnsLocale] - Locale date-fns pour formater la date d'ouverture
 * @param {Object} [props.boundaryRef] - Ref du conteneur bornant l'infobulle (anti-débordement iframe)
 * @return {JSX.Element}
 */
function DayCell({
  day,
  inMonth,
  availabilityMap,
  selectedDate,
  onDateSelect,
  bookingConstraints,
  exceptions = [],
  selectedService = null,
  serviceSchedule = {},
  onShowServiceInfo = null,
  defaultFocusDate = null,
  onKeyDown = null,
  hasFormula = false,
  restaurantOpeningDate = null,
  t = null,
  dateFnsLocale = null,
  boundaryRef = null,
}) {
  const dateKey = format(day, 'yyyy-MM-dd');
  const availability = availabilityMap[dateKey];

  const isServiceNormallyClosedForDay = useCallback((service) => {
    if (!serviceSchedule) return true;

    const dayOfWeek = format(day, 'EEEE').toLowerCase();

    // Si pas de planning pour ce jour du tout, tous les services sont fermés
    if (!serviceSchedule[dayOfWeek] || serviceSchedule[dayOfWeek].length === 0) {
      return true;
    }

    // Vérifier si le service spécifique existe pour ce jour
    return !serviceSchedule[dayOfWeek].some((s) => s.name === service && s.active);
  }, [day, serviceSchedule]);

  const isDayCompletelyClosedNormally = useCallback(() => {
    if (!serviceSchedule) return true;

    const dayOfWeek = format(day, 'EEEE').toLowerCase();

    // Si pas de planning pour ce jour ou si le planning est vide, le jour est complètement fermé
    return !serviceSchedule[dayOfWeek] || serviceSchedule[dayOfWeek].length === 0 ||
           !serviceSchedule[dayOfWeek].some((s) => s.active);
  }, [day, serviceSchedule]);

  const getAffectedServices = useCallback((exception) => {
    if (exception.isClosed) {
      return {
        all: true,
        lunch: {affected: true, explicit: false, closed: true},
        dinner: {affected: true, explicit: false, closed: true},
      };
    }

    if (exception.services && exception.services.length > 0) {
      const isPartial = exception.servicesMode === 'partial';
      const lunchEntry = exception.services.find((s) => s.name === SERVICE_TYPES.LUNCH);
      const dinnerEntry = exception.services.find((s) => s.name === SERVICE_TYPES.DINNER);

      // Classification par moment :
      // - entrée ouverte → service ouvert par l'exception (explicit)
      // - entrée `closed: true` (partiel) → service explicitement fermé ce jour
      // - absent : en partiel, le service suit son planning normal (non affecté) ;
      //   en sémantique historique (replace), absent = fermé.
      const classify = (entry) => {
        if (entry && !entry.closed) return {affected: true, explicit: true, closed: false};
        if (entry && entry.closed) return {affected: true, explicit: false, closed: true};
        return isPartial ?
          {affected: false, explicit: false, closed: false} :
          {affected: true, explicit: false, closed: true};
      };

      return {
        all: false,
        lunch: classify(lunchEntry),
        dinner: classify(dinnerEntry),
      };
    }

    if (exception.openTime && exception.closeTime) {
      return {
        all: false,
        lunch: {affected: true, explicit: true, closed: false},
        dinner: {affected: true, explicit: true, closed: false},
      };
    }

    return {
      all: false,
      lunch: {affected: true, explicit: false, closed: false},
      dinner: {affected: true, explicit: false, closed: false},
    };
  }, []);

  const exceptionInfo = useMemo(() => {
    const exception = findExceptionForDay(exceptions, day);
    if (!exception) return null;

    return {
      exception,
      name: exception.name,
      isClosed: exception.isClosed,
      affectedServices: getAffectedServices(exception),
    };
  }, [day, exceptions, getAffectedServices]);

  // Vérifie si l'autre service (midi/soir) reste ouvert quand le service sélectionné est fermé
  const otherServiceAvailable = useMemo(() => {
    if (!selectedService || !serviceSchedule) return null;

    const dayOfWeek = format(day, 'EEEE').toLowerCase();
    const dayServices = serviceSchedule[dayOfWeek];

    if (!dayServices || dayServices.length === 0) return null;

    const currentServiceClosed = isServiceNormallyClosedForDay(selectedService);
    if (!currentServiceClosed) return null;


    const otherServiceName = selectedService === SERVICE_TYPES.LUNCH ? SERVICE_TYPES.DINNER : SERVICE_TYPES.LUNCH;
    const otherService = dayServices.find((s) => s.name === otherServiceName && s.active);

    if (otherService) {
      return {
        serviceName: otherServiceName,
        serviceLabel: otherServiceName === SERVICE_TYPES.LUNCH ? 'midi' : 'soir',
        hours: `${otherService.start} - ${otherService.end}`,
      };
    }

    return null;
  }, [day, selectedService, serviceSchedule, isServiceNormallyClosedForDay]);

  const exceptionServiceInfo = useMemo(() => {
    if (!exceptionInfo || !exceptionInfo.affectedServices) {
      return {type: 'none', explicit: false, closed: false};
    }

    const services = exceptionInfo.affectedServices;

    if (services.all) {
      return {type: 'all', explicit: false, closed: true};
    }

    if (selectedService === SERVICE_TYPES.LUNCH) {
      if (services.lunch.affected) {
        return {
          type: 'lunch',
          explicit: services.lunch.explicit,
          closed: services.lunch.closed,
        };
      }
    } else if (selectedService === SERVICE_TYPES.DINNER) {
      if (services.dinner.affected) {
        return {
          type: 'dinner',
          explicit: services.dinner.explicit,
          closed: services.dinner.closed,
        };
      }
    } else {
      if (services.lunch.explicit || services.dinner.explicit) {
        if (services.lunch.explicit) {
          return {type: 'lunch', explicit: true, closed: false};
        } else {
          return {type: 'dinner', explicit: true, closed: false};
        }
      } else {
        return {type: 'all', explicit: false, closed: true};
      }
    }

    return {type: 'none', explicit: false, closed: false};
  }, [exceptionInfo, selectedService]);

  // Vrai si l'exception ouvre un jour normalement fermé (ouverture exceptionnelle)
  const exceptionHasPositiveEffect = useMemo(() => {
    if (!exceptionInfo || !exceptionInfo.affectedServices) return false;

    const services = exceptionInfo.affectedServices;

    if (services.lunch && services.lunch.explicit && isServiceNormallyClosedForDay(SERVICE_TYPES.LUNCH)) {
      return true;
    }

    if (services.dinner && services.dinner.explicit && isServiceNormallyClosedForDay(SERVICE_TYPES.DINNER)) {
      return true;
    }

    return false;
  }, [exceptionInfo, isServiceNormallyClosedForDay]);

  // Fermeture implicite : le service n'est pas mentionné dans l'exception mais est normalement ouvert
  const isImplicitExceptionClosure = useMemo(() => {
    if (!exceptionInfo || exceptionServiceInfo.type === 'none' || exceptionInfo.isClosed) {
      return false;
    }

    if (exceptionServiceInfo.closed) {
      if (selectedService) {
        return !isServiceNormallyClosedForDay(selectedService);
      }
    }

    return false;
  }, [exceptionInfo, exceptionServiceInfo, selectedService, isServiceNormallyClosedForDay]);

  // Décide si la date doit être affichée avec le style "exception" (fermeture ou ouverture exceptionnelle)
  const shouldShowAsException = useMemo(() => {
    if (!exceptionInfo || exceptionServiceInfo.type === 'none') return false;
    if (exceptionInfo.isClosed) return true;

    if (selectedService) {
      if (isImplicitExceptionClosure && !isServiceNormallyClosedForDay(selectedService)) return true;
      if (exceptionServiceInfo.explicit && isServiceNormallyClosedForDay(selectedService)) return true;
      if (exceptionServiceInfo.explicit) return true;
    } else {
      if (exceptionHasPositiveEffect) return true;
      if (isImplicitExceptionClosure) {
        return !isServiceNormallyClosedForDay(SERVICE_TYPES.LUNCH) ||
               !isServiceNormallyClosedForDay(SERVICE_TYPES.DINNER);
      }
    }

    return false;
  }, [
    exceptionInfo,
    exceptionServiceInfo,
    selectedService,
    isImplicitExceptionClosure,
    isServiceNormallyClosedForDay,
    exceptionHasPositiveEffect,
  ]);

  const isRegularClosure = useMemo(() => {
    return !shouldShowAsException &&
      (selectedService ? isServiceNormallyClosedForDay(selectedService) : isDayCompletelyClosedNormally());
  }, [shouldShowAsException, selectedService, isServiceNormallyClosedForDay, isDayCompletelyClosedNormally]);

  const {
    isAvailable,
    isDateToday,
    isPastDate,
    isSelected,
    isClosed,
    isExceptionClosed,
    isOutsideBookingWindow,
    isWaitlistOnly,
    isWaitlistBeyondWindow,
    isBeforeOpening,
  } = useMemo(() => {
    const fixedHorizon = isFixedHorizon(bookingConstraints);

    // L'exception du jour est résolue contre le planning hebdomadaire, comme
    // côté serveur ; la règle d'ouverture (ancre service ou minuit) vit dans
    // `isDayOpenForBooking`, partagée avec le bandeau « Prochaines disponibilités ».
    const exception = exceptionInfo && !exceptionInfo.isClosed ? exceptionInfo.exception : null;
    const isBeyondWindow = !isDayOpenForBooking(bookingConstraints, day, {serviceSchedule, selectedService, exception});

    const isDateToday = isToday(day);
    const isPastDate = isPast(day) && !isDateToday;
    const isSelected = selectedDate && isSameDay(day, selectedDate);

    const dayIsClosedRegularly = isRegularClosure;
    const dayIsExceptionClosed = shouldShowAsException && (exceptionInfo?.isClosed || isImplicitExceptionClosure);
    // Nouveau restaurant pas encore ouvert : jour fermé, gris neutre, jamais « complet ».
    const dayIsBeforeOpening = isBeforeOpeningDay(restaurantOpeningDate, day);
    const dayIsClosed = dayIsClosedRegularly || dayIsExceptionClosed || dayIsBeforeOpening;
    const dayIsFull = availability?.status === AVAILABILITY_STATUS.FULL;

    // Jour futur situé au-delà de la fenêtre de réservation (non encore ouvert
    // à la réservation) : à griser, pas à marquer comme "complet". Toute
    // fermeture, exceptionnelle OU hebdomadaire, prime sur cet état :
    // annoncer "réservable à partir du X" sur un jour de congés ou de fermeture
    // hebdomadaire tromperait le client, qui reviendrait à la date d'ouverture
    // pour découvrir que c'est fermé. Même règle pour un jour que l'API annonce
    // déjà complet (service bloqué, privatisation) : il est rendu « complet »
    // tout de suite. Une ouverture exceptionnelle, elle, reste soumise à la
    // fenêtre (rien n'y est réservable de toute façon).
    // En horizon à date butoir fixe, aujourd'hui peut lui-même être hors fenêtre
    // (butoir déjà passé) : le calendrier est alors entièrement fermé.
    const dayIsWaitlistOnly = availability?.status === AVAILABILITY_STATUS.WAITLIST_ONLY;

    // Jour plein mais proposé en liste d'attente, situé hors fenêtre : la liste
    // d'attente n'y est pas encore ouverte (le serveur refuserait l'inscription).
    // Le rendre orange promettrait une inscription impossible, le griser
    // « réservable à partir du X » promettrait une table qui n'existe pas. Il est
    // donc rendu « complet » tout de suite, l'infobulle annonçant l'ouverture de
    // la liste d'attente.
    const dayIsWaitlistBeyondWindow = (!isDateToday || fixedHorizon) &&
      !isPastDate && isBeyondWindow && !dayIsClosed && dayIsWaitlistOnly;

    const isOutsideBookingWindow = (!isDateToday || fixedHorizon) &&
      !isPastDate && isBeyondWindow && !dayIsClosed && !dayIsFull && !dayIsWaitlistBeyondWindow;

    // Pour aujourd'hui, l'API fait autorité (délai minimum, service déjà passé) :
    // le jour est dans la fenêtre dès qu'elle le mentionne, quel que soit le statut.
    const isWithinBookingWindow = isDateToday && !isOutsideBookingWindow ?
      !!availability :
      !isPastDate && !isBeyondWindow;

    let dayIsAvailable = false;

    if (!dayIsClosed && isWithinBookingWindow && !isPastDate) {
      // NOTICE_CLOSED est volontairement rendu comme disponible : le créneau n'est
      // pas complet, seul le canal en ligne est coupé — c'est la sélection de la
      // date qui révèle l'invitation à réserver par téléphone (DateStep).
      // Aujourd'hui suit le même statut que les autres jours : l'API mentionne
      // aussi les jours complets (`full`), sa seule présence ne vaut plus « libre ».
      if (availability?.status === AVAILABILITY_STATUS.AVAILABLE ||
          availability?.status === AVAILABILITY_STATUS.LIMITED ||
          availability?.status === AVAILABILITY_STATUS.BUSY ||
          availability?.status === AVAILABILITY_STATUS.NOTICE_CLOSED) {
        dayIsAvailable = true;
      }
    }

    return {
      isAvailable: dayIsAvailable,
      isDateToday,
      isPastDate,
      isSelected,
      isClosed: dayIsClosed,
      // Avant l'ouverture, le gris neutre l'emporte sur le rendu rouge d'une fermeture exceptionnelle.
      isExceptionClosed: dayIsExceptionClosed && !dayIsBeforeOpening,
      isOutsideBookingWindow,
      isWaitlistOnly: !dayIsClosed && !isPastDate && isWithinBookingWindow && dayIsWaitlistOnly,
      isWaitlistBeyondWindow: dayIsWaitlistBeyondWindow,
      isBeforeOpening: dayIsBeforeOpening,
    };
  }, [
    day,
    restaurantOpeningDate,
    availability,
    selectedDate,
    bookingConstraints,
    serviceSchedule,
    selectedService,
    exceptionInfo,
    shouldShowAsException,
    isImplicitExceptionClosure,
    isRegularClosure,
  ]);

  // Date à laquelle ce jour entrera dans la fenêtre glissante de réservation.
  // Un jour hors fenêtre devient réservable dès que `aujourd'hui` atteint
  // `jour - advanceBookingDays` (le calendrier avançant d'un jour chaque jour).
  // Avec l'ancre 'service_start', l'ouverture a en plus une HEURE précise (celle
  // du service) : `timeLabel` la porte, et `daysUntil` peut valoir 0 (ouverture
  // plus tard dans la journée). En horizon à date butoir fixe, aucune date
  // d'ouverture n'est connue (elle dépend d'une prolongation manuelle du
  // restaurateur) : `fixed` le signale pour adapter l'infobulle. Un jour complet
  // en liste d'attente hors fenêtre partage ce calcul : sa date d'ouverture est
  // celle de la liste d'attente.
  const outsideBookingInfo = useMemo(() => {
    if (!isOutsideBookingWindow && !isWaitlistBeyondWindow) {
      return null;
    }

    if (isFixedHorizon(bookingConstraints)) {
      return {fixed: true};
    }

    const exception = exceptionInfo && !exceptionInfo.isClosed ? exceptionInfo.exception : null;
    const openingInstant = getDayOpeningInstant(bookingConstraints, day, {serviceSchedule, selectedService, exception});
    const openingDate = openingInstant ?
      startOfDay(openingInstant) :
      startOfDay(addDays(day, -bookingConstraints.advanceBookingDays));
    const rawDaysUntil = differenceInCalendarDays(openingDate, startOfDay(new Date()));
    const daysUntil = openingInstant ? Math.max(0, rawDaysUntil) : Math.max(1, rawDaysUntil);
    const dateLabel = format(openingDate, 'EEEE d MMMM', dateFnsLocale ? {locale: dateFnsLocale} : undefined);
    const timeLabel = openingInstant ? format(openingInstant, 'HH:mm') : null;

    return {openingDate, daysUntil, dateLabel, timeLabel};
  }, [isOutsideBookingWindow, isWaitlistBeyondWindow, day, bookingConstraints, serviceSchedule, selectedService, exceptionInfo, dateFnsLocale]);

  const handleClick = (e) => {
    // Un jour hors fenêtre ou avant l'ouverture n'est jamais actionnable.
    if (isOutsideBookingWindow || isBeforeOpening) {
      return;
    }

    if (isExceptionClosed && exceptionInfo && onShowServiceInfo) {
      onShowServiceInfo({
        day,
        isException: true,
        exceptionName: exceptionInfo.name,
        anchorEl: e.currentTarget,
      });
      return;
    }

    if (otherServiceAvailable && isRegularClosure && onShowServiceInfo) {
      onShowServiceInfo({
        day,
        serviceLabel: otherServiceAvailable.serviceLabel,
        hours: otherServiceAvailable.hours,
        anchorEl: e.currentTarget,
      });
      return;
    }

    if ((isAvailable || isWaitlistOnly) && inMonth && (!shouldShowAsException || !exceptionInfo?.isClosed) && onDateSelect) {
      onDateSelect(day);
    }
  };

  const renderDayIcon = () => {
    // Hors période d'ouverture du calendrier ou avant l'ouverture : cellule grisée sans badge
    if (isOutsideBookingWindow || isBeforeOpening) {
      return null;
    }

    if (isExceptionClosed) {
      return (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: -5,
            right: -5,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NotInterestedIcon sx={{fontSize: 12, color: 'error.main'}} aria-hidden="true" />
        </Box>
      );
    }

    if (isWaitlistOnly && !isPastDate) {
      return (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            width: 14,
            height: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <HourglassEmptyIcon sx={{fontSize: 10, color: 'orange.light'}} aria-hidden="true" />
        </Box>
      );
    }

    if (otherServiceAvailable && isRegularClosure && !isPastDate) {
      return (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: -2,
            right: -2,
          }}
        >
          <InfoOutlinedIcon sx={{fontSize: 11, color: 'text.disabled'}} aria-hidden="true" />
        </Box>
      );
    }

    if (hasFormula && !isPastDate && (isAvailable || isWaitlistOnly)) {
      return (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            width: 14,
            height: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AutoAwesomeIcon sx={{fontSize: 10, color: 'purple.dark'}} aria-hidden="true" />
        </Box>
      );
    }

    return null;
  };

  if (!inMonth) {
    return (
      <Box sx={{
        position: 'relative',
        width: 36,
        height: 36,
        mx: 'auto',
        my: 0.3,
      }}>
        <CalendarDayStyle
          inMonth={inMonth}
          tabIndex={-1}
          aria-hidden="true"
        >
          {format(day, 'd')}
        </CalendarDayStyle>
      </Box>
    );
  }

  // Un jour hors période d'ouverture du calendrier reste totalement inerte (ni clic ni popover)
  const isClickableForInfo = !isOutsideBookingWindow && !isBeforeOpening &&
    ((otherServiceAvailable && isRegularClosure && !isPastDate) || (isExceptionClosed && !isPastDate));

  const getAccessibleLabel = () => {
    const dateStr = format(day, 'EEEE d MMMM');
    const parts = [dateStr];

    if (isDateToday) parts.push('aujourd\'hui');
    if (isSelected) parts.push('sélectionné');

    if (isWaitlistOnly) {
      parts.push('liste d\'attente disponible');
    } else if (isAvailable) {
      parts.push('disponible');
    } else if (isBeforeOpening) {
      parts.push(t ? t('widgets.reservation.preOpening.dayBeforeOpening') : 'bientôt, avant l\'ouverture');
    } else if (isExceptionClosed) {
      parts.push('fermé exceptionnellement');
    } else if (isClosed) {
      parts.push('fermé');
    } else if (isPastDate) {
      parts.push('date passée');
    } else if (isOutsideBookingWindow) {
      parts.push(outsideBookingInfo?.dateLabel ?
        `non encore ouvert à la réservation, réservable à partir du ${outsideBookingInfo.dateLabel}${outsideBookingInfo.timeLabel ? ` à ${outsideBookingInfo.timeLabel}` : ''}` :
        'non encore ouvert à la réservation');
    } else if (isWaitlistBeyondWindow) {
      parts.push(outsideBookingInfo?.dateLabel ?
        `complet, liste d'attente possible à partir du ${outsideBookingInfo.dateLabel}` :
        'complet, liste d\'attente pas encore ouverte');
    } else {
      parts.push('complet');
    }

    return parts.join(', ');
  };

  const cell = (
    <Box
      onClick={isClickableForInfo ? handleClick : undefined}
      sx={{
        position: 'relative',
        width: 36,
        height: 36,
        mx: 'auto',
        my: 0.3,
        cursor: isClickableForInfo ? 'pointer' : undefined,
      }}
      role="gridcell"
    >
      <CalendarDayStyle
        onClick={!isClickableForInfo ? handleClick : undefined}
        isToday={isDateToday}
        isSelected={isSelected}
        isAvailable={isAvailable}
        isPast={isPastDate}
        isClosed={isClosed}
        isExceptionClosed={isExceptionClosed}
        isOutsideBookingWindow={isOutsideBookingWindow}
        isWaitlistOnly={isWaitlistOnly}
        inMonth={inMonth}
        disabled={isPastDate}
        disableRipple={isOutsideBookingWindow}
        aria-label={getAccessibleLabel()}
        aria-pressed={isSelected}
        aria-current={isDateToday ? 'date' : undefined}
        aria-disabled={isPastDate || (!isAvailable && !isWaitlistOnly)}
        tabIndex={(isSelected || (defaultFocusDate && isSameDay(day, defaultFocusDate))) ? 0 : -1}
        data-date={dateKey}
      >
        <span aria-hidden="true">{format(day, 'd')}</span>
      </CalendarDayStyle>
      {renderDayIcon()}
    </Box>
  );

  // Infobulles d'ouverture (jour hors fenêtre, jour complet en liste d'attente
  // hors fenêtre) : révélées au survol (desktop) comme au tap (mobile, via
  // enterTouchDelay=0).
  const openingTooltipSlotProps = {
    tooltip: {
      sx: {maxWidth: 240, borderRadius: 1.5, px: 1.75, py: 1.25},
    },
    popper: {
      modifiers: [
        // Bascule au-dessous si le haut manque de place (premières lignes
        // du mois). La place se mesure dans le conteneur du calendrier, pas
        // dans la fenêtre : l'en-tête du widget est collé en haut au-dessus
        // de tout (zIndex 9999), une bulle jugée « visible » dans la fenêtre
        // peut donc passer dessous et se retrouver tronquée.
        // Surtout PAS de `altAxis` sur preventOverflow : il
        // « pousserait » la bulle par-dessus la cellule tapée au lieu de
        // laisser flip la placer dessous, la bulle recouvrait alors le
        // point de contact et les événements souris de compatibilité du
        // tap la refermaient aussitôt (bulle invisible sur mobile).
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom'],
            boundary: boundaryRef?.current || undefined,
            padding: 8,
          },
        },
        // Décale l'infobulle horizontalement vers l'intérieur pour ne
        // jamais sortir du conteneur du calendrier (bord d'iframe).
        {
          name: 'preventOverflow',
          options: {
            boundary: boundaryRef?.current || undefined,
            padding: 8,
          },
        },
      ],
    },
  };

  if (isWaitlistBeyondWindow && outsideBookingInfo) {
    const openingLabel = outsideBookingInfo.dateLabel ?
      outsideBookingInfo.dateLabel + (outsideBookingInfo.timeLabel ?
        ` ${t ? t('widgets.common.bookingOpeningAtTime', {time: outsideBookingInfo.timeLabel}) : `à ${outsideBookingInfo.timeLabel}`}` :
        '') :
      null;

    return (
      <Tooltip
        arrow
        placement="top"
        enterTouchDelay={0}
        leaveTouchDelay={4000}
        slotProps={openingTooltipSlotProps}
        title={
          <Box sx={{textAlign: 'center', py: 0.25}}>
            <Typography variant="caption" sx={{display: 'block', fontWeight: 600, lineHeight: 1.45}}>
              {outsideBookingInfo.fixed || !openingLabel ?
                (t ? t('widgets.common.waitlistNotYetOpenFixed') : 'Complet. Liste d\'attente pas encore ouverte pour cette date') :
                (t ? t('widgets.common.waitlistOpensFrom', {date: openingLabel}) : `Complet. Liste d'attente possible à partir du ${openingLabel}`)}
            </Typography>
          </Box>
        }
      >
        {cell}
      </Tooltip>
    );
  }

  if (isOutsideBookingWindow && outsideBookingInfo) {
    return (
      <Tooltip
        arrow
        placement="top"
        enterTouchDelay={0}
        leaveTouchDelay={4000}
        slotProps={openingTooltipSlotProps}
        title={
          <Box sx={{textAlign: 'center', py: 0.25}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5, opacity: 0.7}}>
              <ScheduleRoundedIcon sx={{fontSize: 13}} />
              <Typography variant="caption" sx={{typography: 'micro', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1}}>
                {t ? t('widgets.common.bookingOpeningTitle') : 'Ouverture des réservations'}
              </Typography>
            </Box>
            {outsideBookingInfo.fixed ? (
              // Horizon à date butoir fixe : pas de date d'ouverture annonçable,
              // elle dépend d'une prolongation manuelle du restaurateur.
              <Typography variant="caption" sx={{display: 'block', fontWeight: 600, lineHeight: 1.45}}>
                {t ?
                  t('widgets.common.bookingNotYetOpenFixed') :
                  'Les réservations ne sont pas encore ouvertes pour cette date'}
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{display: 'block', fontWeight: 700, lineHeight: 1.4}}>
                  {outsideBookingInfo.dateLabel.charAt(0).toUpperCase() + outsideBookingInfo.dateLabel.slice(1)}
                  {outsideBookingInfo.timeLabel &&
                    (t ?
                      ` ${t('widgets.common.bookingOpeningAtTime', {time: outsideBookingInfo.timeLabel})}` :
                      ` à ${outsideBookingInfo.timeLabel}`)}
                </Typography>
                {outsideBookingInfo.daysUntil >= 1 && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      mt: 0.75,
                      px: 1,
                      py: 0.25,
                      borderRadius: 999,
                      bgcolor: (theme) => alpha(theme.palette.common.white, 0.14),
                      typography: 'micro',
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {t ?
                      t('widgets.common.bookingOpensIn', {count: outsideBookingInfo.daysUntil}) :
                      `dans ${outsideBookingInfo.daysUntil} jour${outsideBookingInfo.daysUntil > 1 ? 's' : ''}`}
                  </Box>
                )}
              </>
            )}
          </Box>
        }
      >
        {cell}
      </Tooltip>
    );
  }

  return cell;
}

DayCell.propTypes = {
  day: PropTypes.instanceOf(Date).isRequired,
  inMonth: PropTypes.bool.isRequired,
  availabilityMap: PropTypes.object.isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  onDateSelect: PropTypes.func.isRequired,
  bookingConstraints: PropTypes.object.isRequired,
  exceptions: PropTypes.array,
  selectedService: PropTypes.string,
  serviceSchedule: PropTypes.object,
  onShowServiceInfo: PropTypes.func,
  defaultFocusDate: PropTypes.instanceOf(Date),
  onKeyDown: PropTypes.func,
  hasFormula: PropTypes.bool,
  restaurantOpeningDate: PropTypes.string,
  t: PropTypes.func,
  dateFnsLocale: PropTypes.object,
  boundaryRef: PropTypes.object,
};


// ----------------------------------------------------------------------

/**
 * Calendrier mensuel de réservation avec navigation clavier, légende et popover informatif.
 *
 * @param {Object} props
 * @param {Date} props.currentMonth - Mois affiché
 * @param {Object} props.availabilityMap - Map date (yyyy-MM-dd) vers statut de disponibilité
 * @param {Date} [props.selectedDate] - Date sélectionnée
 * @param {Function} props.onDateSelect - Callback de sélection de date
 * @param {Object} props.bookingConstraints - Contraintes (horizonMode, advanceBookingDays, openUntilDate, minimumNoticeHours)
 * @param {Array} [props.exceptions] - Exceptions de fermeture/ouverture
 * @param {string} [props.selectedService] - Service actif (lunch/dinner)
 * @param {Object} [props.serviceSchedule] - Planning hebdomadaire des services
 * @param {Array} [props.weekdayLabels] - Labels traduits pour les jours de la semaine
 * @param {boolean} [props.waitlistEnabled] - Active l'affichage de la légende liste d'attente
 * @param {Set} [props.formulaDates] - Ensemble de dates (yyyy-MM-dd) ayant des formules temporelles
 * @param {string|null} [props.restaurantOpeningDate] - Jour d'ouverture (yyyy-MM-dd) d'un nouveau restaurant : jours antérieurs grisés
 * @param {Function} [props.t] - Fonction de traduction générique
 * @param {React.Ref} ref - Ref exposant une méthode `focus()` pour le roving tabindex
 */
export const ReservationCalendar = React.forwardRef(function ReservationCalendar({
  currentMonth,
  availabilityMap,
  selectedDate,
  onDateSelect,
  bookingConstraints,
  exceptions = [],
  selectedService = null,
  serviceSchedule = {},
  weekdayLabels = WEEKDAYS, // Utiliser WEEKDAYS par défaut pour la rétrocompatibilité
  waitlistEnabled = false,
  formulaDates = new Set(),
  restaurantOpeningDate = null,
  t, // Translation function
}, ref) {
  const {currentLang} = useLocales();
  const dateFnsLocale = currentLang?.adapterLocale;
  const calendarRef = useRef(null);
  const [serviceInfoPopover, setServiceInfoPopover] = useState(null);
  // Conserve le contenu du popover durant son animation de fermeture (évite un flash vide)
  const [popoverContent, setPopoverContent] = useState(null);
  const [focusedDate, setFocusedDate] = useState(null);

  const handleShowServiceInfo = useCallback((info) => {
    const fullInfo = {
      ...info,
      anchorEl: calendarRef.current,
    };
    setServiceInfoPopover(fullInfo);
    setPopoverContent(fullInfo);
  }, []);

  const handleCloseServiceInfo = useCallback(() => {
    setServiceInfoPopover(null);
  }, []);

  const calendarDays = useMemo(() => {
    const firstDayOfMonth = startOfMonth(currentMonth);
    const lastDayOfMonth = endOfMonth(currentMonth);

    // Index lundi-base (0 = lundi, 6 = dimanche) pour aligner la grille
    const firstDayIndex = (firstDayOfMonth.getDay() + 6) % 7;

    const totalDays = 7 * Math.ceil((lastDayOfMonth.getDate() + firstDayIndex) / 7);

    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayIndex);

    return Array.from({length: totalDays}, (_, i) => {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);

      const inMonth = getMonth(date) === getMonth(currentMonth) &&
                       getYear(date) === getYear(currentMonth);

      return {
        date,
        inMonth,
      };
    });
  }, [currentMonth]);

  // Présence d'au moins une icône de formule sur les jours visibles du mois
  // courant, conditionne l'affichage de la ligne de légende dédiée.
  const hasFormulaInCurrentMonth = useMemo(() => {
    if (!formulaDates || formulaDates.size === 0) return false;
    return calendarDays.some(({date, inMonth}) =>
      inMonth && formulaDates.has(format(date, 'yyyy-MM-dd')),
    );
  }, [calendarDays, formulaDates]);

  // Priorité : focusedDate > selectedDate > aujourd'hui > premier jour du mois
  const defaultFocusDate = useMemo(() => {
    if (focusedDate) {
      const focusedInMonth = getMonth(focusedDate) === getMonth(currentMonth) &&
                             getYear(focusedDate) === getYear(currentMonth);
      if (focusedInMonth) return focusedDate;
    }

    if (selectedDate) {
      const selectedInMonth = getMonth(selectedDate) === getMonth(currentMonth) &&
                              getYear(selectedDate) === getYear(currentMonth);
      if (selectedInMonth) return selectedDate;
    }

    const today = new Date();
    const todayInMonth = getMonth(today) === getMonth(currentMonth) &&
                         getYear(today) === getYear(currentMonth);
    if (todayInMonth) return today;

    return startOfMonth(currentMonth);
  }, [focusedDate, selectedDate, currentMonth]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      const dateKey = format(defaultFocusDate, 'yyyy-MM-dd');
      const element = calendarRef.current?.querySelector(`[data-date="${dateKey}"]`);
      if (element) {
        element.focus();
      }
    },
  }), [defaultFocusDate]);

  const handleCalendarKeyDown = useCallback((event) => {
    const currentFocus = defaultFocusDate;
    if (!currentFocus) return;

    let newDate = null;
    let handled = false;

    switch (event.key) {
      case 'ArrowLeft':
        newDate = addDays(currentFocus, -1);
        handled = true;
        break;
      case 'ArrowRight':
        newDate = addDays(currentFocus, 1);
        handled = true;
        break;
      case 'ArrowUp':
        newDate = addDays(currentFocus, -7);
        handled = true;
        break;
      case 'ArrowDown':
        newDate = addDays(currentFocus, 7);
        handled = true;
        break;
      case 'Home':
        newDate = startOfMonth(currentMonth);
        handled = true;
        break;
      case 'End':
        newDate = endOfMonth(currentMonth);
        handled = true;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (onDateSelect) {
          onDateSelect(currentFocus);
        }
        return;
      default:
        return;
    }

    if (handled && newDate) {
      event.preventDefault();
      event.stopPropagation();

      const newDateInMonth = getMonth(newDate) === getMonth(currentMonth) &&
                             getYear(newDate) === getYear(currentMonth);

      if (newDateInMonth) {
        setFocusedDate(newDate);

        setTimeout(() => {
          const dateKey = format(newDate, 'yyyy-MM-dd');
          const element = calendarRef.current?.querySelector(`[data-date="${dateKey}"]`);
          if (element) {
            element.focus();
          }
        }, 0);
      }
    }
  }, [defaultFocusDate, currentMonth, onDateSelect]);

  const calendarAriaLabel = `Calendrier de ${format(currentMonth, 'MMMM yyyy')}`;

  return (
    <Box
      ref={calendarRef}
      role="grid"
      aria-label={calendarAriaLabel}
    >
      <Grid container spacing={0} sx={{mb: 0.5}} role="row">
        {weekdayLabels.map((day, index) => (
          <Grid item xs={12/7} key={index} role="columnheader">
            <Typography
              component="abbr"
              title={day.full}
              align="center"
              variant="subtitle2"
              color="text.secondary"
              sx={{
                fontWeight: 'bold',
                textDecoration: 'none',
                display: 'block',
                textAlign: 'center',
              }}
            >
              {day.abbr}
            </Typography>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={0} role="rowgroup" onKeyDown={handleCalendarKeyDown}>
        {calendarDays.map(({date, inMonth}, index) => (
          <Grid item xs={12/7} key={index} role="presentation">
            <DayCell
              day={date}
              inMonth={inMonth}
              availabilityMap={availabilityMap}
              selectedDate={selectedDate}
              onDateSelect={onDateSelect}
              bookingConstraints={bookingConstraints}
              exceptions={exceptions}
              selectedService={selectedService}
              serviceSchedule={serviceSchedule}
              onShowServiceInfo={handleShowServiceInfo}
              defaultFocusDate={defaultFocusDate}
              onKeyDown={handleCalendarKeyDown}
              hasFormula={formulaDates.has(format(date, 'yyyy-MM-dd'))}
              restaurantOpeningDate={restaurantOpeningDate}
              t={t}
              dateFnsLocale={dateFnsLocale}
              boundaryRef={calendarRef}
            />
          </Grid>
        ))}
      </Grid>

      <Box
        role="presentation"
        aria-hidden="true"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          mt: 1.5,
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'success.main',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t ? t('widgets.common.calendarLegend.available') : 'Disponible'}
          </Typography>
        </Box>
        {waitlistEnabled && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'orange.light',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t ? t('widgets.common.calendarLegend.waitlist') : 'Liste d\'attente'}
            </Typography>
          </Box>
        )}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'error.main',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t ? t('widgets.common.calendarLegend.full') : 'Complet'}
          </Typography>
        </Box>
        {hasFormulaInCurrentMonth && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            <AutoAwesomeIcon sx={{fontSize: 12, color: 'purple.dark'}} aria-hidden="true" />
            <Typography variant="caption" color="text.secondary">
              {t ? t('widgets.common.calendarLegend.formula') : 'Formule'}
            </Typography>
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(serviceInfoPopover)}
        anchorEl={serviceInfoPopover?.anchorEl}
        onClose={handleCloseServiceInfo}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.1)',
              bgcolor: 'background.paper',
              minWidth: 280,
              maxWidth: 340,
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.common.black, 0.06),
            },
          },
        }}
      >
        <Box sx={{p: 3, position: 'relative'}}>
          <IconButton
            onClick={handleCloseServiceInfo}
            size="small"
            aria-label="Fermer l'information"
            sx={{
              'position': 'absolute',
              'top': 12,
              'right': 12,
              'color': 'text.secondary',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.04),
              },
            }}
          >
            <CloseIcon sx={{fontSize: 16}} aria-hidden="true" />
          </IconButton>

          {popoverContent?.isException ? (
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <NotInterestedIcon sx={{fontSize: 20, color: 'error.main'}} aria-hidden="true" />
              </Box>
              <Box sx={{pt: 0.5, pr: 2}}>
                <Typography variant="subtitle1" component="h3" sx={{fontWeight: 600, letterSpacing: -0.2, mb: 0.5}}>
                  Fermé exceptionnellement
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
                  {popoverContent?.exceptionName}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <InfoOutlinedIcon sx={{fontSize: 20, color: 'success.main'}} aria-hidden="true" />
              </Box>
              <Box sx={{pt: 0.5, pr: 2}}>
                <Typography variant="subtitle1" component="h3" sx={{fontWeight: 600, letterSpacing: -0.2, mb: 0.5}}>
                  Ouvert {popoverContent?.serviceLabel === 'midi' ? 'le midi' : 'le soir'} uniquement
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
                  {popoverContent?.hours}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
});

ReservationCalendar.propTypes = {
  currentMonth: PropTypes.instanceOf(Date).isRequired,
  availabilityMap: PropTypes.object.isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  onDateSelect: PropTypes.func.isRequired,
  bookingConstraints: PropTypes.object.isRequired,
  exceptions: PropTypes.array,
  selectedService: PropTypes.string,
  serviceSchedule: PropTypes.object,
  weekdayLabels: PropTypes.arrayOf(PropTypes.shape({
    abbr: PropTypes.string,
    full: PropTypes.string,
  })),
  waitlistEnabled: PropTypes.bool,
  formulaDates: PropTypes.instanceOf(Set),
  restaurantOpeningDate: PropTypes.string,
  t: PropTypes.func,
};

